import { RefObject, useEffect } from 'react';

const SENTINEL = { __pdfPreviewOpen: true } as const;

const isHorizontalWheel = (e: WheelEvent): boolean => Math.abs(e.deltaX) > Math.abs(e.deltaY);

const isHistoryShortcut = (e: KeyboardEvent): boolean =>
  (e.metaKey || e.altKey) && (e.key === 'ArrowLeft' || e.key === 'ArrowRight');

const isMultiTouch = (e: TouchEvent): boolean => e.touches.length > 1;

const preventIf =
  <E extends Event>(matches: (e: E) => boolean) =>
  (e: E): void => {
    if (matches(e)) e.preventDefault();
  };

const reEnterSentinel = (): void => {
  window.history.pushState(SENTINEL, '');
};

export function usePdfPreviewNavigationGuard(wrapperRef: RefObject<HTMLElement>): void {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overscrollBehaviorX;
    const prevBody = body.style.overscrollBehaviorX;
    html.style.overscrollBehaviorX = 'none';
    body.style.overscrollBehaviorX = 'none';

    reEnterSentinel();

    const ac = new AbortController();
    const { signal } = ac;
    const nonPassive: AddEventListenerOptions = { passive: false, signal };

    window.addEventListener('popstate', reEnterSentinel, { signal });
    window.addEventListener('keydown', preventIf(isHistoryShortcut), { signal });

    const wrapper = wrapperRef.current;
    wrapper?.addEventListener('wheel', preventIf(isHorizontalWheel), nonPassive);
    wrapper?.addEventListener('touchmove', preventIf(isMultiTouch), nonPassive);

    return () => {
      ac.abort();
      html.style.overscrollBehaviorX = prevHtml;
      body.style.overscrollBehaviorX = prevBody;
      if (window.history.state?.__pdfPreviewOpen) {
        window.history.back();
      }
    };
  }, [wrapperRef]);
}
